import { useEffect, useState } from "react";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AdSlot } from "@/components/ads/AdSlot";
import { API } from "@/hooks/useAuth";
import { Trophy } from "lucide-react";

export function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/games/leaderboard`)
      .then((res) => setRows(res.data.leaderboard || []))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const medal = (rank) => {
    if (rank === 1) return "bg-amber-100 text-amber-900 border-amber-300";
    if (rank === 2) return "bg-stone-100 text-stone-700 border-stone-300";
    if (rank === 3) return "bg-orange-100 text-orange-900 border-orange-300";
    return "bg-muted/50 text-foreground border-border";
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Global ranking</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-1 flex items-center gap-3">
          <Trophy className="h-8 w-8 text-amber-600" /> Leaderboard
        </h1>
        <p className="text-foreground/70 mt-2 max-w-2xl">
          Rated online matches use the Glicko-2 system. Beat stronger opponents to climb faster.
        </p>

        <div className="my-6">
          <AdSlot zoneId="leaderboard-top" size="leaderboard" />
        </div>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-muted-foreground">Loading…</p>
          ) : error ? (
            <p className="p-8 text-center text-destructive">{error}</p>
          ) : rows.length === 0 ? (
            <p data-testid="leaderboard-empty" className="p-8 text-center text-muted-foreground">
              No rated matches yet. Be the first — challenge another player in the{" "}
              <a href="/lobby" className="text-primary hover:underline">Lobby</a>.
            </p>
          ) : (
            <table data-testid="leaderboard-table" className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Player</th>
                  <th className="px-4 py-3 text-right">Rating</th>
                  <th className="px-4 py-3 text-right hidden sm:table-cell">Played</th>
                  <th className="px-4 py-3 text-right hidden sm:table-cell">W / L / D</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-border/40">
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`rounded-full border ${medal(r.rank)}`}>
                        {r.rank}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold">{r.display_name || r.username}</p>
                      <p className="text-xs text-muted-foreground">@{r.username} · {r.country || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.rating}
                      <span className="block text-[10px] text-muted-foreground">±{r.deviation}</span>
                    </td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{r.played}</td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell font-mono">
                      {r.wins} / {r.losses} / {r.draws}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </section>
    </Layout>
  );
}
